import type {ReactElement, ReactNode} from 'react';
import {useRef} from 'react';
import React, {useEffect, useLayoutEffect} from 'react';
import {arrow, autoUpdate, flip, FloatingArrow, type Middleware, offset, shift, useDismiss, useFloating, useInteractions, useRole} from '@floating-ui/react';
import {ARROW_HEIGHT, ARROW_WIDTH, PopoverDefaultAlignment, ARROW_STROKE_OFFSET, ARROW_STROKE_WIDTH, ARROW_TIP_RADIUS} from '@Components/popover/popover.types';
import {isEntirelyInViewport} from '@Utils/browser.util';
import styles from './popover.module.scss';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';

const DEFAULT_OFFSET = 4;

export interface PopoverProps {
  referenceElementRef: React.RefObject<HTMLElement | null>;
  children: ReactNode;
  popoverCloseFunction: () => void;
  alignment?: PopoverDefaultAlignment;
  popoverOffset?: number;
  scrollAnchorIntoView?: boolean;
  className?: string;
  isPositionFixed?: boolean;
  shouldInheritReferenceElementWidth?: boolean;
  hasArrow?: boolean;
}

export function Popover({
  referenceElementRef,
  children,
  popoverCloseFunction,
  alignment = PopoverDefaultAlignment.BOTTOM_START,
  popoverOffset = DEFAULT_OFFSET,
  scrollAnchorIntoView = true,
  className = '',
  isPositionFixed = true,
  shouldInheritReferenceElementWidth = false,
  hasArrow = false,
}: PopoverProps): ReactElement<any> {
  const smoothScrollAnchorIntoViewIfNeeded = (): void => {
    const doesAnchorNeedToBeScrolledIntoView = scrollAnchorIntoView && referenceElementRef.current && !isEntirelyInViewport(referenceElementRef);
    if (doesAnchorNeedToBeScrolledIntoView) {
      referenceElementRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  const getMiddleware = (): Middleware[] => {
    const middleware: Middleware[] = [offset(popoverOffset + (hasArrow ? ARROW_HEIGHT : 0)), flip(), shift()];
    if (hasArrow) {
      middleware.push(arrow({element: hasArrowRef}));
    }

    return middleware;
  };

  const hasArrowRef = useRef(null);
  const {x, y, refs, context, strategy, placement} = useFloating({
    open: true,
    onOpenChange: popoverCloseFunction,
    strategy: isPositionFixed ? 'fixed' : 'absolute',
    placement: alignment,
    middleware: getMiddleware(),
    whileElementsMounted: autoUpdate,
  });

  useLayoutEffect(() => {
    refs.setReference(referenceElementRef.current);
  }, [refs, referenceElementRef.current]);

  useEffect(() => {
    smoothScrollAnchorIntoViewIfNeeded();
  }, []);

  const dismiss = useDismiss(context);
  const role = useRole(context);

  // Merge all the interactions into prop getters
  const {getFloatingProps} = useInteractions([dismiss, role]);

  const getWidth = (): number | undefined => {
    if (shouldInheritReferenceElementWidth && referenceElementRef.current) {
      return referenceElementRef.current.offsetWidth;
    }
    return undefined;
  };

  const getStaticOffset = (): string | null => {
    return placement.split('-')[1] === 'start' ? '6px' : null;
  };

  return (
    <div
      ref={refs.setFloating}
      style={{
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
        width: getWidth(),
      }}
      className={`${styles.popover} ${className}`}
      {...getFloatingProps()}
    >
      {hasArrow ? (
        <FloatingArrow
          ref={hasArrowRef}
          context={context}
          width={ARROW_WIDTH}
          height={ARROW_HEIGHT}
          stroke={PMW_COLORS_NEUTRAL.NEUTRAL_6}
          fill={PMW_COLORS_NEUTRAL.NEUTRAL_0}
          tipRadius={ARROW_TIP_RADIUS}
          strokeWidth={ARROW_STROKE_WIDTH}
          startOffset={ARROW_STROKE_OFFSET}
          strokeLinejoin="round"
          strokeLinecap="round"
          staticOffset={getStaticOffset()}
        />
      ) : null}
      {children}
    </div>
  );
}
